'use client';

import { useEffect, useRef, useState } from 'react';
import { isMobile } from '@/lib/utils';

/** Returns perceived luminance 0–255 from an rgb/rgba string */
function getLuminance(rgb: string): number {
  const match = rgb.match(/\d+(\.\d+)?/g);
  if (!match || match.length < 3) return -1;
  const [r, g, b] = match.map(Number);
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * Use elementsFromPoint() to get ALL stacked elements at (x, y),
 * then find the first one with a non-transparent background.
 * This naturally X-rays through transparent headers, overlays, etc.
 */
function getBgLuminanceAt(x: number, y: number, excludeEl: HTMLElement): number {
  const elements = document.elementsFromPoint(x, y) as HTMLElement[];

  for (const el of elements) {
    if (el === excludeEl) continue; // skip the cursor ball itself
    if (el === document.documentElement) continue;

    const bg = getComputedStyle(el).backgroundColor;
    if (!bg || bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') continue;

    const lum = getLuminance(bg);
    if (lum >= 0) return lum;
  }

  // Final fallback: check <html> computed bg
  const htmlBg = getComputedStyle(document.documentElement).backgroundColor;
  const htmlLum = getLuminance(htmlBg);
  if (htmlLum >= 0 && htmlBg !== 'rgba(0, 0, 0, 0)') return htmlLum;

  // If everything is transparent, default to light (safe for white pages)
  return 255;
}

export function CustomCursor() {
  const ballRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [mounted, setMounted] = useState(false);
  const currentModeRef = useRef<'light' | 'dark'>('dark');

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted || isMobile()) return;

    // GSAP is dynamic-imported so this layout-level component doesn't put it
    // in every page's entry chunk (TASK-4). On touch devices the early return
    // above means GSAP is never even fetched.
    let cancelled = false;
    let cleanup: (() => void) | undefined;

    (async () => {
    const { gsap } = await import('@/lib/gsap');
    if (cancelled) return;

    const ball = ballRef.current;
    if (!ball) return;

    gsap.set(ball, { xPercent: -50, yPercent: -50, scale: 0.5, opacity: 0 });

    let hasStarted = false;
    let moveCount = 0;
    const CHECK_EVERY = 4; // check luminance every Nth mousemove

    const handleMouseMove = (e: MouseEvent) => {
      const { clientX: x, clientY: y } = e;

      if (!hasStarted) {
        hasStarted = true;
        gsap.to(ball, { scale: 1, opacity: 1, duration: 0.5, ease: 'elastic.out(1, 0.5)' });
      }

      gsap.to(ball, { x, y, duration: 0.55, ease: 'power3.out', overwrite: 'auto' });

      // Throttled dynamic contrast detection
      if (++moveCount % CHECK_EVERY === 0) {
        // Temporarily hide ball so elementsFromPoint doesn't hit it
        ball.style.visibility = 'hidden';
        const lum = getBgLuminanceAt(x, y, ball);
        ball.style.visibility = '';

        const wantedMode = lum > 128 ? 'light' : 'dark';
        if (wantedMode !== currentModeRef.current) {
          currentModeRef.current = wantedMode;
          gsap.to(ball, {
            borderColor: wantedMode === 'light' ? '#111111' : '#ffffff',
            duration: 0.2,
            ease: 'power2.out',
          });
        }
      }
    };

    const handleMouseDown = () => gsap.to(ball, { scale: 0.75, duration: 0.15, ease: 'power2.out', overwrite: 'auto' });
    const handleMouseUp = () => gsap.to(ball, { scale: 1, duration: 0.4, ease: 'elastic.out(1, 0.4)', overwrite: 'auto' });
    const handleMouseLeave = () => gsap.to(ball, { opacity: 0, duration: 0.3 });
    const handleMouseEnter = () => gsap.to(ball, { opacity: 1, duration: 0.3 });

    const label = labelRef.current;

    // Contextual "label mode" — elements with [data-cursor-label] turn the
    // cursor into a filled pill showing the label (e.g. "View" on project
    // thumbnails). This is the cursor-as-navigation microinteraction.
    const enterLabel = (text: string) => {
      if (label) label.textContent = text;
      gsap.to(ball, {
        width: 76,
        height: 76,
        backgroundColor: 'rgba(10,10,10,0.55)',
        borderColor: 'var(--color-accent)',
        duration: 0.3,
        ease: 'power3.out',
        overwrite: 'auto',
      });
      gsap.to(label, { opacity: 1, duration: 0.25, ease: 'power2.out' });
    };
    const exitLabel = () => {
      gsap.to(ball, {
        width: 36,
        height: 36,
        backgroundColor: 'transparent',
        borderColor: currentModeRef.current === 'light' ? '#111111' : '#ffffff',
        scale: 1,
        duration: 0.3,
        ease: 'power3.out',
        overwrite: 'auto',
      });
      gsap.to(label, { opacity: 0, duration: 0.2, ease: 'power2.out' });
    };

    // Hover behaviour on interactive elements. Elements with a
    // [data-cursor-label] use label mode; everything else just expands.
    const addHoverListeners = () => {
      document.querySelectorAll('a, button, [data-cursor-hover], [data-cursor-label], input, textarea, select, label').forEach((el) => {
        const cursorLabel = el.getAttribute('data-cursor-label');
        if (cursorLabel) {
          el.addEventListener('mouseenter', () => enterLabel(cursorLabel));
          el.addEventListener('mouseleave', exitLabel);
        } else {
          el.addEventListener('mouseenter', () => gsap.to(ball, { scale: 2.2, duration: 0.3, ease: 'power2.out' }));
          el.addEventListener('mouseleave', () => gsap.to(ball, { scale: 1, duration: 0.3, ease: 'power2.out' }));
        }
      });
    };
    const hoverTimer = setTimeout(addHoverListeners, 300);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);

    cleanup = () => {
      clearTimeout(hoverTimer);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
    };
    })();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [mounted]);

  if (!mounted || isMobile()) return null;

  return (
    <div id="magic-cursor" className="pointer-events-none fixed left-0 top-0 z-[9999]">
      <div
        id="ball"
        ref={ballRef}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          border: '1.5px solid #ffffff',
          backgroundColor: 'transparent',
          position: 'fixed',
          top: 0,
          left: 0,
          willChange: 'transform',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          ref={labelRef}
          style={{
            opacity: 0,
            color: '#ffffff',
            fontFamily: 'var(--font-mono), ui-monospace, monospace',
            fontSize: '11px',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        />
      </div>
    </div>
  );
}
